import React, { useEffect, useRef } from "react"
import styles from "./services.module.scss"

function BlackHoleCanvas() {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    let animationId
    let isVisible = true

    // Mouse coordinates tracker
    let mouse = {
      x: null,
      y: null,
      targetX: null,
      targetY: null,
      active: false,
      radius: 250,
    }

    // Resize handler
    const resizeCanvas = () => {
      const rect = canvas.parentElement.getBoundingClientRect()
      canvas.width = rect.width
      canvas.height = rect.height
    }
    resizeCanvas()
    window.addEventListener("resize", resizeCanvas)

    const handleMouseMove = e => {
      const rect = canvas.getBoundingClientRect()
      mouse.targetX = e.clientX - rect.left
      mouse.targetY = e.clientY - rect.top
      mouse.active = true
    }

    const handleMouseLeave = () => {
      mouse.active = false
    }

    const parent = canvas.parentElement
    parent.addEventListener("mousemove", handleMouseMove)
    parent.addEventListener("mouseleave", handleMouseLeave)

    // ========================================================
    // COSMIC NEBULA CONFIGURATION
    // ========================================================
    const particles = []
    const particleCount = 850 // Higher density for richer textures
    const colors = [
      "rgba(129, 140, 248, ", // Bright Indigo
      "rgba(240, 76, 239, ", // Bright Magenta
      "rgba(34, 211, 238, ", // Bright Cyan
      "rgba(251, 191, 36, ", // Bright Gold
      "rgba(167, 139, 250, ", // Bright Violet
    ]

    class Particle {
      constructor() {
        this.reset()
        this.angle = Math.random() * Math.PI * 2
        this.radius =
          40 + Math.random() * (Math.max(canvas.width, canvas.height) * 0.45)
      }

      reset() {
        this.angle = Math.random() * Math.PI * 2
        this.radius = 10 + Math.random() * 45
        this.speed = 0.002 + Math.random() * 0.006
        this.radialSpeed = 0.2 + Math.random() * 0.7
        this.size = 0.5 + Math.random() * 1.5
        this.baseColor = colors[Math.floor(Math.random() * colors.length)]
        this.alpha = 0.25 + Math.random() * 0.55 // Brighter opacity range
        this.decay = 0.0003 + Math.random() * 0.001
        this.wobblePhase = Math.random() * Math.PI * 2
      }

      update(centerX, centerY) {
        this.angle += this.speed
        this.radius += this.radialSpeed
        this.alpha -= this.decay
        this.wobblePhase += 0.02

        // Mouse gravity pull
        if (mouse.active && mouse.x !== null) {
          const dx = mouse.x - (centerX + Math.cos(this.angle) * this.radius)
          const dy = mouse.y - (centerY + Math.sin(this.angle) * this.radius)
          const dist = Math.sqrt(dx * dx + dy * dy)

          if (dist < mouse.radius) {
            const force = (mouse.radius - dist) / mouse.radius
            this.angle += force * 0.02
            this.radius += (mouse.x > centerX ? -force : force) * 0.45
          }
        }

        if (
          this.alpha <= 0 ||
          this.radius > Math.max(canvas.width, canvas.height) * 0.55
        ) {
          this.reset()
        }
      }

      draw(ctx, centerX, centerY) {
        const wobble = Math.sin(this.wobblePhase) * 3.0
        const x = centerX + Math.cos(this.angle) * (this.radius + wobble)
        const y = centerY + Math.sin(this.angle) * (this.radius + wobble)

        ctx.beginPath()
        ctx.arc(x, y, this.size, 0, Math.PI * 2)
        ctx.fillStyle = this.baseColor + this.alpha + ")"
        ctx.fill()
      }
    }

    for (let i = 0; i < particleCount; i++) {
      particles.push(new Particle())
    }

    // Large glowing color blobs (increased opacity and radius)
    const nebulaClouds = [
      {
        x: 0,
        y: 0,
        rx: 140,
        ry: 160,
        color: "rgba(99, 102, 241, 0.26)",
        speedX: 0.001,
        speedY: 0.002,
        phase: 0,
      },
      {
        x: 0,
        y: 0,
        rx: 170,
        ry: 130,
        color: "rgba(217, 70, 239, 0.22)",
        speedX: 0.002,
        speedY: 0.001,
        phase: 1.5,
      },
      {
        x: 0,
        y: 0,
        rx: 160,
        ry: 150,
        color: "rgba(6, 182, 212, 0.24)",
        speedX: 0.001,
        speedY: 0.0015,
        phase: 3,
      },
      {
        x: 0,
        y: 0,
        rx: 200,
        ry: 110,
        color: "rgba(139, 92, 246, 0.18)",
        speedX: 0.0015,
        speedY: 0.001,
        phase: 4.5,
      },
    ]

    // ========================================================
    // SPACE ORBITS AND PLANETARY SYSTEM
    // ========================================================
    const orbits = [
      {
        radiusX: 130,
        radiusY: 70,
        rotation: -Math.PI / 12,
        speed: 0.008,
        color: "rgba(99, 102, 241, 0.4)", // Brighter orbit
        drawEntity: (ctx, x, y, angle) => {
          const zDepth = Math.sin(angle)
          const scale = 0.85 + zDepth * 0.25
          ctx.save()
          ctx.translate(x, y)
          ctx.scale(scale, scale)
          ctx.rotate(-Math.PI / 8)

          // Glowing Saturn Ring
          ctx.shadowBlur = 8
          ctx.shadowColor = "rgba(251, 191, 36, 0.8)"
          ctx.beginPath()
          ctx.ellipse(0, 0, 9, 3, 0, 0, Math.PI * 2)
          ctx.strokeStyle = "rgba(251, 191, 36, 0.8)"
          ctx.lineWidth = 1.8
          ctx.stroke()
          ctx.shadowBlur = 0

          // Saturn Sphere
          const satGrad = ctx.createRadialGradient(-1, -1, 0, 0, 0, 5)
          satGrad.addColorStop(0, "#fef08a")
          satGrad.addColorStop(1, "#d97706")
          ctx.beginPath()
          ctx.arc(0, 0, 4.8, 0, Math.PI * 2)
          ctx.fillStyle = satGrad
          ctx.fill()
          ctx.restore()
        },
      },
      {
        radiusX: 220,
        radiusY: 110,
        rotation: Math.PI / 8,
        speed: -0.005,
        color: "rgba(6, 182, 212, 0.35)", // Brighter orbit
        drawEntity: (ctx, x, y, angle, time) => {
          const zDepth = Math.sin(angle)
          const scale = 0.85 + zDepth * 0.22
          ctx.save()
          ctx.translate(x, y)
          ctx.scale(scale, scale)

          // Glowing Earth
          ctx.shadowBlur = 10
          ctx.shadowColor = "rgba(56, 189, 248, 0.6)"
          const earthGrad = ctx.createRadialGradient(-1.2, -1.2, 0, 0, 0, 6)
          earthGrad.addColorStop(0, "#38bdf8")
          earthGrad.addColorStop(0.7, "#0284c7")
          earthGrad.addColorStop(1, "#0369a1")
          ctx.beginPath()
          ctx.arc(0, 0, 5.5, 0, Math.PI * 2)
          ctx.fillStyle = earthGrad
          ctx.fill()
          ctx.shadowBlur = 0

          // Green land
          ctx.fillStyle = "rgba(74, 222, 128, 0.7)"
          ctx.beginPath()
          ctx.arc(-1.5, -1, 1.5, 0, Math.PI * 2)
          ctx.arc(1.5, 1.2, 1.2, 0, Math.PI * 2)
          ctx.fill()

          // Moon
          const moonAngle = time * 2.5
          const mx = 11 * Math.cos(moonAngle)
          const my = 4 * Math.sin(moonAngle)
          ctx.beginPath()
          ctx.arc(mx, my, 1.2, 0, Math.PI * 2)
          ctx.fillStyle = "#e2e8f0"
          ctx.fill()
          ctx.restore()
        },
      },
      {
        radiusX: 300,
        radiusY: 150,
        rotation: -Math.PI / 16,
        speed: 0.004,
        color: "rgba(217, 70, 239, 0.25)",
        drawEntity: (ctx, x, y, angle, time) => {
          const zDepth = Math.sin(angle)
          const scale = 0.8 + zDepth * 0.18
          ctx.save()
          ctx.translate(x, y)
          ctx.scale(scale, scale)
          ctx.rotate(time * 0.6)

          // Satellite Hexagon Body
          ctx.fillStyle = "#cbd5e1"
          ctx.beginPath()
          for (let i = 0; i < 6; i++) {
            const sa = (i / 6) * Math.PI * 2
            const sx = 3 * Math.cos(sa)
            const sy = 3 * Math.sin(sa)
            if (i === 0) ctx.moveTo(sx, sy)
            else ctx.lineTo(sx, sy)
          }
          ctx.closePath()
          ctx.fill()

          // Solar panels
          ctx.fillStyle = "rgba(6, 182, 212, 0.95)"
          ctx.fillRect(-8, -1, 4, 2)
          ctx.fillRect(4, -1, 4, 2)

          // Antenna Blinker
          ctx.beginPath()
          ctx.arc(0, -5, 1.0, 0, Math.PI * 2)
          ctx.fillStyle = "#f43f5e"
          ctx.shadowBlur = 6
          ctx.shadowColor = "#f43f5e"
          ctx.globalAlpha = Math.sin(time * 8) > 0 ? 1 : 0.2
          ctx.fill()
          ctx.restore()
        },
      },
    ]

    orbits.forEach(orbit => {
      orbit.angle = Math.random() * Math.PI * 2
    })

    // ========================================================
    // SPACESHIP (ENDURANCE) SLINGSHOT SIMULATOR
    // ========================================================
    class Spaceship {
      constructor() {
        this.a = 165
        this.b = 75
        this.e = 0.82
        this.meanAnomaly = -Math.PI
        this.orbitSpeed = 0.012
        this.trail = []
        this.maxTrailLength = 65
      }

      update() {
        this.meanAnomaly += this.orbitSpeed
        if (this.meanAnomaly > Math.PI) {
          this.meanAnomaly = -Math.PI
          this.trail = []
        }

        let E = this.meanAnomaly
        for (let i = 0; i < 5; i++) {
          E =
            E -
            (E - this.e * Math.sin(E) - this.meanAnomaly) /
              (1 - this.e * Math.cos(E))
        }

        const ox = this.a * (Math.cos(E) - this.e)
        const oz = this.b * Math.sin(E)
        const oy = Math.sin(E * 2) * 5

        this.x = ox
        this.y = oy
        this.z = oz

        const dE_dt = 1.0 / (1 - this.e * Math.cos(E))
        this.vx = -this.a * Math.sin(E) * dE_dt
        this.vz = this.b * Math.cos(E) * dE_dt
        this.vy = 2 * Math.cos(E * 2) * dE_dt

        this.trail.push({ x: this.x, y: this.y, z: this.z })
        if (this.trail.length > this.maxTrailLength) {
          this.trail.shift()
        }
      }

      draw(ctx, centerX, centerY, tiltAngle, time) {
        // Draw Thruster Trail (cyan engine energy)
        this.trail.forEach((pos, idx) => {
          const alpha = (idx / this.maxTrailLength) * 0.45
          const cosX = Math.cos(tiltAngle)
          const sinX = Math.sin(tiltAngle)
          const yTilted = pos.y * cosX - pos.z * sinX

          ctx.beginPath()
          ctx.arc(centerX + pos.x, centerY + yTilted, 1.0, 0, Math.PI * 2)
          ctx.fillStyle = `rgba(56, 189, 248, ${alpha})`
          ctx.fill()
        })

        // Ship tilt coordinates
        const cosX = Math.cos(tiltAngle)
        const sinX = Math.sin(tiltAngle)
        const yTilted = this.y * cosX - this.z * sinX
        const zTilted = this.y * sinX + this.z * cosX

        // Scale by depth
        const scale = 0.6 + ((zTilted + 75) / 150) * 0.8
        const dist = Math.sqrt(
          this.x * this.x + this.y * this.y + this.z * this.z
        )
        const stress = dist < 90 ? (90 - dist) / 50 : 0
        const shakeX = (Math.random() - 0.5) * stress * 2.5
        const shakeY = (Math.random() - 0.5) * stress * 2.5

        ctx.save()
        ctx.translate(centerX + this.x + shakeX, centerY + yTilted + shakeY)
        ctx.scale(scale, scale)

        const angle2D = Math.atan2(this.vy * cosX - this.vz * sinX, this.vx)
        ctx.rotate(angle2D)

        const moduleCount = 12
        const ringRadius = 5.2
        ctx.shadowBlur = 6
        ctx.shadowColor = "rgba(56, 189, 248, 0.8)"

        for (let i = 0; i < moduleCount; i++) {
          const ma = (i / moduleCount) * Math.PI * 2
          const mx = ringRadius * Math.cos(ma)
          const my = ringRadius * Math.sin(ma)

          ctx.fillStyle = "#cbd5e1"
          if (i === 0) {
            ctx.beginPath()
            ctx.rect(mx - 1.2, my - 1.2, 2.4, 3.0)
            ctx.fillStyle = "#e2e8f0"
            ctx.fill()
          } else {
            ctx.beginPath()
            ctx.arc(mx, my, 0.9, 0, Math.PI * 2)
            ctx.fill()
          }
        }
        ctx.shadowBlur = 0

        // Thruster flame
        const flameLength = 5 + Math.random() * 8 * (1 + stress)
        ctx.beginPath()
        ctx.moveTo(0, ringRadius)
        ctx.lineTo(-1.5, ringRadius + flameLength)
        ctx.lineTo(1.5, ringRadius + flameLength)
        ctx.closePath()

        const flameGrad = ctx.createLinearGradient(
          0,
          ringRadius,
          0,
          ringRadius + flameLength
        )
        flameGrad.addColorStop(0, "rgba(56, 189, 248, 0.95)")
        flameGrad.addColorStop(1, "rgba(0, 0, 0, 0)")
        ctx.fillStyle = flameGrad
        ctx.fill()

        ctx.restore()
      }
    }

    const spaceship = new Spaceship()
    const tiltAngle = (18 * Math.PI) / 180
    let time = 0

    // Intersection Observer
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          isVisible = entry.isIntersecting
        })
      },
      { threshold: 0.05 }
    )
    observer.observe(canvas)

    // Animation Loop
    const animate = () => {
      if (!isVisible) {
        animationId = requestAnimationFrame(animate)
        return
      }

      time += 0.02
      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      spaceship.update()

      // Calculate camera shake based on ship close approach
      const dist = Math.sqrt(
        spaceship.x * spaceship.x +
          spaceship.y * spaceship.y +
          spaceship.z * spaceship.z
      )
      let camShake = 0
      if (dist < 85) {
        camShake = (85 - dist) / 45
      }
      const camX = (Math.random() - 0.5) * camShake * 2.2
      const camY = (Math.random() - 0.5) * camShake * 2.2

      ctx.save()
      ctx.translate(camX, camY)

      const isLightTheme =
        typeof document !== "undefined" &&
        document.documentElement.classList.contains("light-theme")

      // 1. Dark/Light Space Background
      ctx.fillStyle = isLightTheme ? "#f8fafc" : "#040409" // Dynamic background
      ctx.fillRect(-10, -10, canvas.width + 20, canvas.height + 20)

      // 2. Render Nebula Gas Clouds with Brightened Radial Gradients
      ctx.save()
      ctx.globalCompositeOperation = isLightTheme ? "source-over" : "screen"
      nebulaClouds.forEach(cloud => {
        cloud.phase += 0.003
        const offsetX = Math.sin(cloud.phase * 1.1) * 80
        const offsetY = Math.cos(cloud.phase * 0.7) * 50

        let mousePushX = 0
        let mousePushY = 0
        if (mouse.active && mouse.x !== null) {
          mousePushX = (mouse.x - centerX) * 0.12
          mousePushY = (mouse.y - centerY) * 0.12
        }

        const cloudX = centerX + offsetX + mousePushX
        const cloudY = centerY + offsetY + mousePushY

        // Dynamic pulse to cloud brightness
        const pulse = 1.0 + Math.sin(time * 0.8) * 0.1
        const grad = ctx.createRadialGradient(
          cloudX,
          cloudY,
          0,
          cloudX,
          cloudY,
          Math.max(cloud.rx, cloud.ry) * 2.5
        )
        grad.addColorStop(0, cloud.color)
        grad.addColorStop(0.5, cloud.color.replace("0.", "0.06"))
        grad.addColorStop(1, "rgba(0, 0, 0, 0)")

        ctx.fillStyle = grad
        ctx.beginPath()
        ctx.arc(
          cloudX,
          cloudY,
          Math.max(cloud.rx, cloud.ry) * 2.5 * pulse,
          0,
          Math.PI * 2
        )
        ctx.fill()
      })
      ctx.restore()

      // 3. Draw Concentric Space Orbits with a Soft Glow
      orbits.forEach(orbit => {
        ctx.save()
        ctx.translate(centerX, centerY)
        ctx.rotate(orbit.rotation)

        ctx.shadowBlur = 4
        ctx.shadowColor = orbit.color
        ctx.beginPath()
        ctx.ellipse(0, 0, orbit.radiusX, orbit.radiusY, 0, 0, Math.PI * 2)
        ctx.strokeStyle = orbit.color
        ctx.lineWidth = 1.2
        ctx.setLineDash([5, 10])
        ctx.stroke()

        ctx.restore()
      })

      // 4. Update and Draw swarming dust particles
      particles.forEach(p => {
        p.update(centerX, centerY)
        p.draw(ctx, centerX, centerY)
      })

      // 5. Draw Orbiting Planets
      orbits.forEach(orbit => {
        orbit.angle += orbit.speed
        const rawX = orbit.radiusX * Math.cos(orbit.angle)
        const rawY = orbit.radiusY * Math.sin(orbit.angle)
        const cosRot = Math.cos(orbit.rotation)
        const sinRot = Math.sin(orbit.rotation)
        const entityX = centerX + (rawX * cosRot - rawY * sinRot)
        const entityY = centerY + (rawX * sinRot + rawY * cosRot)

        orbit.drawEntity(ctx, entityX, entityY, orbit.angle, time)
      })

      // 6. Draw Spaceship (Endurance slingshot) in front of background
      spaceship.draw(ctx, centerX, centerY, tiltAngle, time)

      // 7. Glow gravity center (Brighter central core)
      const coreGrad = ctx.createRadialGradient(
        centerX,
        centerY,
        0,
        centerX,
        centerY,
        170
      )
      coreGrad.addColorStop(0, "rgba(99, 102, 241, 0.22)")
      coreGrad.addColorStop(0.35, "rgba(217, 70, 239, 0.08)")
      coreGrad.addColorStop(0.85, "rgba(6, 182, 212, 0.02)")
      coreGrad.addColorStop(1, "rgba(0, 0, 0, 0)")
      ctx.fillStyle = coreGrad
      ctx.beginPath()
      ctx.arc(centerX, centerY, 170, 0, Math.PI * 2)
      ctx.fill()

      ctx.restore()

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      cancelAnimationFrame(animationId)
      window.removeEventListener("resize", resizeCanvas)
      parent.removeEventListener("mousemove", handleMouseMove)
      parent.removeEventListener("mouseleave", handleMouseLeave)
      observer.disconnect()
    }
  }, [])

  return <canvas ref={canvasRef} className={styles.blackHoleCanvas} />
}

export default BlackHoleCanvas
